import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, UserPlus, KeyRound, UserCog, Ban, CheckCircle2, Trash2, Pencil } from "lucide-react";

const ROLES = ["super_admin", "admin", "project_manager", "technician", "supervisor", "customer"];
const LANGS = [["en", "English"], ["es", "Español"]];

export default function UsersPage() {
  const { user: me, can } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", language: "en", company_id: "" });
  const [newPw, setNewPw] = useState("");
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "technician", company_id: "", language: "en" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); api.get("/companies").then((r) => setCompanies(r.data)); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/users", { ...form, company_id: form.company_id || null });
      toast.success(t("users.userCreated"));
      setOpen(false);
      setForm({ email: "", name: "", password: "", role: "technician", company_id: "", language: "en" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    toast.success(u.active ? t("users.userDisabled") : t("users.userEnabled"));
    load();
  };

  const setPassword = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${pwUser.id}/set-password`, { new_password: newPw });
      toast.success(t("users.passwordReset"));
      setPwUser(null); setNewPw("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteUser = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${delUser.id}`);
      toast.success(t("users.deleted"));
      setDelUser(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = (u) => {
    setEditForm({ name: u.name || "", role: u.role, language: u.language || "en", company_id: u.company_id || "" });
    setEditUser(u);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${editUser.id}`, {
        name: editForm.name,
        role: editForm.role,
        language: editForm.language,
        company_id: ["supervisor", "customer"].includes(editForm.role) ? (editForm.company_id || null) : null,
      });
      toast.success(t("users.userUpdated"));
      setEditUser(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!users)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-black text-slate-900">{t("users.title")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="gap-2 bg-blue-600 hover:bg-blue-500"><UserPlus className="h-4 w-4" /> {t("users.newUser")}</Button>
          </DialogTrigger>
          <DialogContent className="border-slate-200 bg-white">
            <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("users.createUser")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="overline text-slate-500">{t("users.name")}</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div><Label className="overline text-slate-500">{t("users.email")}</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div><Label className="overline text-slate-500">{t("users.password")}</Label><Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div>
                <Label className="overline text-slate-500">{t("users.role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.filter((r) => r !== "super_admin" || me.role === "super_admin").map((r) => <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">{t("users.language")}</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="user-language" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map(([code, label]) => <SelectItem key={code} value={code} data-testid={`user-lang-${code}`}>{label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {["supervisor", "customer"].includes(form.role) && (
                <div>
                  <Label className="overline text-slate-500">{t("users.company")}</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger data-testid="user-company" className="mt-1.5 border-slate-300 bg-white"><SelectValue placeholder={t("users.selectCompany")} /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="user-save" onClick={create} disabled={busy || !form.email || !form.name || !form.password} className="bg-blue-600 hover:bg-blue-500">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("users.create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3">
        {users.map((u) => (
          <Card key={u.id} data-testid={`user-${u.id}`} className="flex flex-wrap items-center justify-between gap-3 border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-600"><UserCog className="h-5 w-5" /></div>
              <div>
                <div className="font-medium text-slate-900">{u.name} {!u.active && <span className="text-xs text-red-500">{t("users.disabled")}</span>}</div>
                <div className="text-sm text-slate-500">{u.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="overline border-0 bg-blue-100 text-blue-700">{t(`roles.${u.role}`)}</Badge>
              {can("users.manage") && (
                <Button size="sm" variant="ghost" data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="gap-1.5 text-slate-600"><Pencil className="h-4 w-4" /> {t("users.editBtn")}</Button>
              )}
              <Button size="sm" variant="ghost" data-testid={`reset-pw-${u.id}`} onClick={() => setPwUser(u)} className="gap-1.5 text-slate-600"><KeyRound className="h-4 w-4" /> {t("users.passwordBtn")}</Button>
              <Button size="sm" variant="ghost" data-testid={`toggle-${u.id}`} onClick={() => toggleActive(u)} className={u.active ? "gap-1.5 text-red-500" : "gap-1.5 text-emerald-600"}>
                {u.active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              </Button>
              {can("users.delete") && u.id !== me.id && (
                <Button size="sm" variant="ghost" data-testid={`delete-user-${u.id}`} onClick={() => setDelUser(u)} className="gap-1.5 text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" /> {t("users.deleteBtn")}
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!pwUser} onOpenChange={() => { setPwUser(null); setNewPw(""); }}>
        <DialogContent className="border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("users.resetPwTitle", { name: pwUser?.name })}</DialogTitle></DialogHeader>
          <Input data-testid="set-password-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder={t("users.newPasswordPlaceholder")} className="border-slate-300 bg-white" />
          <DialogFooter>
            <Button data-testid="set-password-save" onClick={setPassword} disabled={busy || newPw.length < 6} className="bg-blue-600 hover:bg-blue-500">{t("users.updatePassword")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delUser} onOpenChange={() => setDelUser(null)}>
        <DialogContent className="border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("users.deleteTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("users.deleteConfirm", { name: delUser?.name, email: delUser?.email })}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelUser(null)} className="border-slate-300">{t("users.cancel")}</Button>
            <Button data-testid="confirm-delete-user" onClick={deleteUser} disabled={busy} className="gap-1.5 bg-red-600 hover:bg-red-500">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("users.deleteBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("users.editTitle", { name: editUser?.name })}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline text-slate-500">{t("users.name")}</Label><Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
            <div>
              <Label className="overline text-slate-500">{t("users.role")}</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.filter((r) => r !== "super_admin" || me.role === "super_admin").map((r) => <SelectItem key={r} value={r} data-testid={`edit-role-${r}`}>{t(`roles.${r}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-slate-500">{t("users.language")}</Label>
              <Select value={editForm.language} onValueChange={(v) => setEditForm({ ...editForm, language: v })}>
                <SelectTrigger data-testid="edit-user-language" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map(([code, label]) => <SelectItem key={code} value={code}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {["supervisor", "customer"].includes(editForm.role) && (
              <div>
                <Label className="overline text-slate-500">{t("users.company")}</Label>
                <Select value={editForm.company_id} onValueChange={(v) => setEditForm({ ...editForm, company_id: v })}>
                  <SelectTrigger data-testid="edit-user-company" className="mt-1.5 border-slate-300 bg-white"><SelectValue placeholder={t("users.selectCompany")} /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="border-slate-300">{t("users.cancel")}</Button>
            <Button data-testid="edit-user-save" onClick={saveEdit} disabled={busy || !editForm.name || !editForm.role} className="bg-blue-600 hover:bg-blue-500">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("users.saveBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
